import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

// Extend the default sanitize schema so we can render:
//   • our YouTube-embed div (<div class="youtube-embed" data-youtube-id="…">)
//   • the `class` attribute (Tailwind classnames on admin-authored HTML)
//   • target/rel on links
// Everything else (script tags, on* handlers, javascript: URLs, iframes we
// don't control, dangerous style/svg vectors) is stripped by default.
const SAFE_SCHEMA = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    div: [
      ...(defaultSchema.attributes?.div || []),
      "className", "class", "dataYoutubeId",
      ["data-youtube-id"],
    ],
    a: [
      ...(defaultSchema.attributes?.a || []),
      "target", "rel", "className",
    ],
    img: [
      ...(defaultSchema.attributes?.img || []),
      "loading", "className",
    ],
    "*": [...(defaultSchema.attributes?.["*"] || []), "className"],
  },
  // Restrict URL protocols: no javascript:, data:, vbscript: etc.
  protocols: {
    ...defaultSchema.protocols,
    href: ["http", "https", "mailto", "tel"],
    src: ["http", "https"],
  },
};

// Renders admin-authored markdown with safe HTML. Raw HTML is allowed
// (rehypeRaw) but then sanitized (rehypeSanitize) so a compromised admin
// account can't inject <script> or on-handlers that fire in visitor browsers.
export default function MarkdownView({ children, className = "" }) {
  if (!children) return null;
  return (
    <div className={`markdown-view ${className}`}>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, [rehypeSanitize, SAFE_SCHEMA]]}
        components={{
          h1: (p) => <h1 className="font-serif text-3xl md:text-4xl text-ink mt-6 mb-3" {...p} />,
          h2: (p) => <h2 className="font-serif text-2xl md:text-3xl text-ink mt-6 mb-3" {...p} />,
          h3: (p) => <h3 className="font-serif text-xl text-ink mt-4 mb-2" {...p} />,
          p:  (p) => <p className="text-ink/80 leading-relaxed mb-4 font-light" {...p} />,
          a:  (p) => <a className="text-navy underline underline-offset-4 hover:text-gold" target={p.href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" {...p} />,
          ul: (p) => <ul className="list-disc list-inside space-y-1.5 text-ink/80 mb-4 pl-2" {...p} />,
          ol: (p) => <ol className="list-decimal list-inside space-y-1.5 text-ink/80 mb-4 pl-2" {...p} />,
          li: (p) => <li className="leading-relaxed font-light" {...p} />,
          blockquote: (p) => <blockquote className="border-l-4 border-gold pl-5 italic text-ink/75 my-5 font-serif text-lg" {...p} />,
          code: (p) => <code className="bg-sand/60 px-1.5 py-0.5 rounded text-sm" {...p} />,
          img: ({ alt, ...p }) => (
            <img alt={alt || ""} {...p} className="my-6 rounded-2xl w-full h-auto object-cover" loading="lazy" />
          ),
          iframe: (p) => (
            <div className="my-6 aspect-video rounded-2xl overflow-hidden bg-sand">
              <iframe {...p} className="w-full h-full" allowFullScreen />
            </div>
          ),
          // Custom YouTube embed: <div class="youtube-embed" data-youtube-id="…" />
          div: ({ className, ...props }) => {
            const cls = className || "";
            if (cls.includes("youtube-embed") && props["data-youtube-id"]) {
              const id = props["data-youtube-id"];
              return (
                <div className="my-6 aspect-video rounded-2xl overflow-hidden bg-navy shadow-xl shadow-navy/15">
                  <iframe
                    src={`https://www.youtube.com/embed/${id}`}
                    title="YouTube video"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              );
            }
            return <div className={cls} {...props} />;
          },
          hr: () => <hr className="my-8 border-sand" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
